import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { NextAction } from "@/types/dashboard/genie-console";

interface NextActionLineProps {
  action: NextAction | null;
  /** Brand colour of the selected genie, so the line belongs to this genie. */
  brandColor: string;
}

/**
 * The computed do-this-next line (HG-1231).
 *
 * Exactly one sentence and exactly one button, always. The ranking lives in
 * `nextAction.ts` (pure, unit tested) — this component only paints whichever
 * single rule matched. The healthy state carries the same visual weight as
 * every action state: same gradient border, same solid brand button, no
 * greyed-out "nothing to do here" treatment.
 */
export const NextActionLine = ({ action, brandColor }: NextActionLineProps) => {
  if (!action) {
    return (
      <div className="rounded-[16px] border border-genie-border bg-white p-4">
        <Skeleton className="h-5 w-3/4 max-w-md" />
        <Skeleton className="mt-3 h-9 w-44" />
      </div>
    );
  }

  return (
    <div
      className="rounded-[16px] p-[1.5px] transition-all duration-500"
      style={{
        background:
          action.tone === "healthy"
            ? "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)"
            : brandColor,
      }}
    >
      <div className="flex flex-col gap-3 rounded-[14.5px] bg-white px-4 py-3.5 sm:flex-row sm:items-center sm:justify-between sm:gap-6">
        <p className="text-[15px] font-semibold leading-snug text-[#222B45]">
          {action.sentence}
        </p>
        <Link
          to={action.to}
          className="inline-flex flex-shrink-0 items-center gap-2 self-start rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-200 hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-genie-primary focus-visible:ring-offset-2 sm:self-auto"
          style={{
            background:
              action.tone === "healthy"
                ? "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)"
                : brandColor,
          }}
        >
          {action.buttonLabel}
          <ArrowRight aria-hidden="true" className="h-4 w-4" />
        </Link>
      </div>
    </div>
  );
};

export default NextActionLine;
